import { useQuery, UseQueryResult, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { apiClient } from '../../utils/api-client';
import { CalendarBookingsResponse, CalendarBooking, ExternalBooking } from '../../types';
import { mergeBookingsWithExternal, detectBookingConflicts } from '../../utils/calendar-utils';
import { useDebounce } from '../useDebounce';

interface UseCalendarBookingsParams {
  startDate: string;
  endDate: string;
  listingIds?: string[];
  includeExternal?: boolean;
  debounceMs?: number;
}

interface UseCalendarBookingsResult {
  allBookings: CalendarBooking[];
  directBookings: CalendarBooking[];
  externalBookings: ExternalBooking[];
  syncStatus: Record<string, any>;
  conflicts: Array<{
    directBooking: CalendarBooking;
    externalBooking: CalendarBooking;
    conflictType: 'overlap' | 'exact_match';
  }>;
}

interface UseCalendarBookingsReturn {
  // Query result properties
  data: UseCalendarBookingsResult | undefined;
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  isSuccess: boolean;
  refetch: () => Promise<any>;
  // Additional state management methods
  invalidateBookings: () => Promise<void>;
  prefetchNextMonth: (nextMonthStart: string, nextMonthEnd: string) => Promise<void>;
  optimisticUpdateBooking: (bookingId: string, updates: Partial<CalendarBooking>) => void;
  removeOptimisticUpdate: (bookingId: string) => void;
}

// Query key factory for consistent cache management
export const calendarBookingsKeys = {
  all: ['calendar-bookings'] as const,
  lists: () => [...calendarBookingsKeys.all, 'list'] as const,
  list: (params: UseCalendarBookingsParams) => [...calendarBookingsKeys.lists(), params] as const,
  details: () => [...calendarBookingsKeys.all, 'detail'] as const,
  detail: (id: string) => [...calendarBookingsKeys.details(), id] as const,
};

export const useCalendarBookings = (
  params: UseCalendarBookingsParams
): UseCalendarBookingsReturn => {
  const queryClient = useQueryClient();
  
  // Debounce the listing filter changes to prevent excessive API calls
  const debouncedListingIds = useDebounce(params.listingIds, params.debounceMs || 300);
  
  // Create stable query parameters
  const queryParams = useMemo(() => ({
    startDate: params.startDate,
    endDate: params.endDate,
    listingIds: debouncedListingIds,
    includeExternal: params.includeExternal
  }), [params.startDate, params.endDate, debouncedListingIds, params.includeExternal]);

  const query = useQuery({
    queryKey: calendarBookingsKeys.list(queryParams),
    queryFn: async (): Promise<UseCalendarBookingsResult> => {
      const urlParams = new URLSearchParams({
        startDate: queryParams.startDate,
        endDate: queryParams.endDate,
        includeExternal: queryParams.includeExternal ? 'true' : 'false'
      });

      if (queryParams.listingIds && queryParams.listingIds.length > 0) {
        urlParams.append('listingIds', queryParams.listingIds.join(','));
      }

      const response = await apiClient.get<CalendarBookingsResponse>(
        `/bookings/calendar?${urlParams.toString()}`
      );

      const { bookings: directBookings, externalBookings, syncStatus } = response.data;

      // Create listing titles map
      const listingTitles: Record<string, string> = {};
      directBookings.forEach(booking => {
        listingTitles[booking.listingId] = booking.listingTitle;
      });

      // Merge direct and external bookings
      const allBookings = mergeBookingsWithExternal(
        directBookings,
        externalBookings,
        listingTitles
      );

      // Detect conflicts
      const conflicts = detectBookingConflicts(allBookings);

      return {
        allBookings,
        directBookings,
        externalBookings,
        syncStatus,
        conflicts
      };
    },
    enabled: !!(queryParams.startDate && queryParams.endDate),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors, but retry on network errors
      if (error && typeof error === 'object' && 'status' in error && typeof (error as any).status === 'number') {
        return (error as any).status >= 500 && failureCount < 3;
      }
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Additional state management methods
  const invalidateBookings = async () => {
    await queryClient.invalidateQueries({
      queryKey: calendarBookingsKeys.all
    });
  };

  const prefetchNextMonth = async (nextMonthStart: string, nextMonthEnd: string) => {
    const nextMonthParams = {
      ...queryParams,
      startDate: nextMonthStart,
      endDate: nextMonthEnd
    };

    await queryClient.prefetchQuery({
      queryKey: calendarBookingsKeys.list(nextMonthParams),
      queryFn: async (): Promise<UseCalendarBookingsResult> => {
        const urlParams = new URLSearchParams({
          startDate: nextMonthParams.startDate,
          endDate: nextMonthParams.endDate,
          includeExternal: nextMonthParams.includeExternal ? 'true' : 'false'
        });

        if (nextMonthParams.listingIds && nextMonthParams.listingIds.length > 0) {
          urlParams.append('listingIds', nextMonthParams.listingIds.join(','));
        }

        const response = await apiClient.get<CalendarBookingsResponse>(
          `/bookings/calendar?${urlParams.toString()}`
        );

        const { bookings: directBookings, externalBookings, syncStatus } = response.data;

        const listingTitles: Record<string, string> = {};
        directBookings.forEach(booking => {
          listingTitles[booking.listingId] = booking.listingTitle;
        });

        const allBookings = mergeBookingsWithExternal(
          directBookings,
          externalBookings,
          listingTitles
        );

        const conflicts = detectBookingConflicts(allBookings);

        return {
          allBookings,
          directBookings,
          externalBookings,
          syncStatus,
          conflicts
        };
      },
      staleTime: 5 * 60 * 1000,
    });
  };

  const optimisticUpdateBooking = (bookingId: string, updates: Partial<CalendarBooking>) => {
    queryClient.setQueryData(
      calendarBookingsKeys.list(queryParams),
      (oldData: UseCalendarBookingsResult | undefined) => {
        if (!oldData) return oldData;

        const updateBookingInArray = (bookings: CalendarBooking[]) =>
          bookings.map(booking =>
            booking.bookingId === bookingId
              ? { ...booking, ...updates }
              : booking
          );

        return {
          ...oldData,
          allBookings: updateBookingInArray(oldData.allBookings),
          directBookings: updateBookingInArray(oldData.directBookings),
        };
      }
    );
  };

  const removeOptimisticUpdate = (bookingId: string) => {
    // Invalidate and refetch to remove optimistic updates
    queryClient.invalidateQueries({
      queryKey: calendarBookingsKeys.list(queryParams)
    });
  };

  return {
    data: query.data as UseCalendarBookingsResult | undefined,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    isError: query.isError,
    isSuccess: query.isSuccess,
    refetch: query.refetch,
    invalidateBookings,
    prefetchNextMonth,
    optimisticUpdateBooking,
    removeOptimisticUpdate,
  };
};

export default useCalendarBookings;